import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';

import { Button, Card, Text } from 'v3/components';
import { useSnackbar } from 'v3/components/Snackbar';
import { useMultistepForm } from 'hooks/useMultiStepForm';
import { cooperplaceApi } from 'services/api';
import {
  LoadContext,
  checkClientCreditLimit,
} from 'v3/pages/Load/Register/controller';
import { fetchDispositionData } from 'utils/fetches';
import { currencyOptions } from 'v3/utils/formatter';
import { useInsuranceValue } from 'contexts/InsuranceValueContext';
import {
  LoadDataGroSchema,
  LocationsGroSchema,
  VehicleInfosDataSchema,
  vehicleAndImplementTypesAreValid
} from './validators';
import { LoadGroProvider } from './context';
import {
  handleCreate,
  handleInitializeSingleOption,
  validateDestinationDate,
  validateDestinationDateWithOrigin,
  validateDestinationsEndDate,
  validateDestinationsStartDate,
} from './utils';
import {
  Line,
  ProgressBarContainer,
  Step,
  StepContainer,
  StepsContainer,
} from './styles';
import { LoadDataGro } from './components/LoadData';
import { VehiclesAndTracking } from './components/VehiclesAndTracking';
import { LocationCard } from './components/Locations/Locations';
import FreightValuesGRO from './components/FreightValuesGRO';
import { validateSequentialDates } from 'v3/components/TravelRouteCard/Validation';
import moment from 'moment';
import { DELIVERY_TYPE_OPTIONS } from 'v3/utils/constants';

import {
  loadReplacementToFreightData,
  loadToFreightData,
} from 'v3/components/FreightValues/utils';

const [, useLoad] = LoadContext;

export function RegisterLoadGRO() {
  const snackbar = useSnackbar();
  const location = useLocation();
  const [freightValuesInitiated, setFreightValuesInitiated] = useState(false);
  const [showRouteErrors, setShowRouteErrors] = useState(false);
  const [routeErrorMessage, setRouteErrorMessage] = useState('');
  const [policyRouteErrorPointIds, setPolicyRouteErrorPointIds] = useState([]);
  const [tollsPlazaInfos, setTollsPlazaInfos] = useState();
  const [tollsPlazaLoading, setTollsPlazaLoading] = useState(false);

  const navigate = useNavigate();

  const {
    data,
    setData,
    setErrors,
    extraFields,
    inputsExtraFields,
    transferExtraFields,
    discountsExtraFields,
    loadingGro,
    setLoadingGro,
    setShowModal,
    setRegisteredLoad,
    rateResponse,
    loadingDuplicate,
  } = useLoad();

  const params = useParams();

  const { updateInsuranceValue, loading: insuranceBennerLoading } =
    useInsuranceValue();
  const freightValuesRef = useRef();
  const freightValuesReplaceRef = useRef();
  const hasOrigin = Boolean(data?.origin?.lat && data?.origin?.lng);
  const hasDestination = Array.isArray(data?.destinations) && data.destinations.some(d => typeof d?.lat === 'number' && !isNaN(d.lat) && typeof d?.lng === 'number' && !isNaN(d.lng));
  const isValidPoint = (p) => p && typeof p.lat === 'number' && !Number.isNaN(p.lat) && typeof p.lng === 'number' && !Number.isNaN(p.lng);

  async function setFreightValuesDuplicateLoad(loadData, rateResponse) {
    const props = loadToFreightData(loadData, loadData?.travel);
    const exchangeDay = String(rateResponse?.buy || 0).padEnd(6, '0');

    props.freightExchange = exchangeDay
    props.exchangeDay = exchangeDay
    props.currency = loadData?.currency
    props.customData.shipmentTransfers.pcpObservation = null

    await freightValuesRef.current?.init({
      ...props,
      rateResponse
    });

    if (loadData?.isReplacement && loadData?.replacements.length > 0) {
      const replacementProps = loadReplacementToFreightData(
        loadData,
        loadData?.travel
      );

      const exchangeDay = String(rateResponse?.buy || 0).padEnd(6, '0');

      replacementProps.freightExchange = exchangeDay
      replacementProps.exchangeDay = exchangeDay
      replacementProps.currency = loadData?.currency
      replacementProps.customData.shipmentTransfers.pcpObservation = null

      await freightValuesReplaceRef.current?.init({
        ...replacementProps,
        rateResponse,
      });
    } else {
      await freightValuesReplaceRef?.current?.init({
        exchangeDay,
        freightExchange: exchangeDay,
        cargoValue: loadData?.cargoValue,
        currency: currencyOptions[0],
        rateResponse: rateResponse,
      });
    }
  }

  useEffect(() => {
    if (params.clone_id && rateResponse) {
      setFreightValuesDuplicateLoad(data, rateResponse);
    }
  }, [params.clone_id, data, rateResponse]);

  async function initFreightValues(rateResponse) {
    const exchangeDay = String(rateResponse?.buy || 0).padEnd(6, '0');
    await freightValuesRef?.current?.init({
      exchangeDay,
      freightExchange: exchangeDay,
      cargoValue: data?.cargoValue,
      currency: currencyOptions[0],
      rateResponse: rateResponse,
    });
    await freightValuesReplaceRef?.current?.init({
      exchangeDay,
      freightExchange: exchangeDay,
      cargoValue: data?.cargoValue,
      currency: currencyOptions[0],
      rateResponse: rateResponse,
    });
  }

  useEffect(() => {
    if (
      location?.pathname.includes('gro') &&
      rateResponse &&
      !freightValuesInitiated
    ) {
      initFreightValues(rateResponse);
      setFreightValuesInitiated(true);
    }
  }, [rateResponse, freightValuesRef?.current, freightValuesInitiated]);

  const { step, steps, back, next, currentStepIndex, isFirstStep, isLastStep } =
    useMultistepForm([
      <LoadDataGro />,
      <LocationCard
        key={`loc-${Number(showRouteErrors)}-${routeErrorMessage}`}
        showRouteErrors={showRouteErrors}
        routeErrorMessage={routeErrorMessage}
        routeErrorPointIds={policyRouteErrorPointIds}
      />,
      <VehiclesAndTracking />,
      <FreightValuesGRO ref={freightValuesRef} />,
    ]);

  const [errorsGro, setErrorsGro] = useState({});
  const [destinationsError, setDestinationsError] = useState({});
  const [operations, setOperations] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedPolicyProduct, setSelectedPolicyProduct] = useState(null);
  const [countryOptions, setCountriesOptions] = useState({
    origin: [],
    destination: [],
  });
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [bodiesTypes, setBodiesTypes] = useState([]);
  const [implementTypes, setImplementTypes] = useState([]);
  const [loadDispositionOptions, setLoadDispositionOptions] = useState([]);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [selectedRange, setSelectedRange] = useState(null);
  const [dailyCotation, setDailyCotation] = useState([]);
  const [debouncedCargo, setDebouncedCargo] = useState('');
  const [loadingRange, setLoadingRange] = useState(false);
  const [managerTrackerOptions, setManagerTrackerOptions] = useState([]);
  const [loadingSelectedPolicy, setLoadingSelectedPolicy] = useState(false);
  const disableProduct = !data?.operation && !products?.length;

  const handleGetSelectedPolicy = useCallback(async id => {
    try {
      setLoadingSelectedPolicy(true);
      const response = await cooperplaceApi.get(`policies/${id}`);
      const policyCurrency = currencyOptions.find(
        currency => currency.value === response?.data?.currency
      );
      setData(old => ({ ...old, load_currency: policyCurrency }));
      setSelectedPolicy(response.data);
    } catch (error) {
      setLoadingSelectedPolicy(false);
      snackbar.show(
        <Text>Falha ao recuperar dados da apólice do cliente!</Text>,
        {
          type: 'error',
        }
      );
      navigate('/cargas');
    } finally {
      setLoadingSelectedPolicy(false);
    }
  }, []);

  async function handleGetClientOperations(policies) {
    try {
      const response = await cooperplaceApi.get(`policies/operations`, {
        params: {
          policyIds: policies?.map(item => item?.id),
        },
      });
      if (response?.data?.length === 1) {
        handleInitializeSingleOption(response?.data, 'operation', setData);
        await handleGetSelectedPolicy(response?.data?.[0]?.policy?.id);
      }
      setOperations(response.data);
    } catch (error) {
      snackbar.show(
        <Text>Falha ao consultar operações disponíveis para o cliente!</Text>,
        {
          type: 'error',
        }
      );
      navigate('/cargas');
    }
  }

  function handleSelectProduct(value) {
    if (value) {
      const selectedNcmId = value?.productNcmId;
      let selectedPolicyPd = null;
      selectedPolicy.operations
        .filter(operation => operation?.id === data?.operation?.id)
        .forEach(operation => {
          operation.products.forEach(product => {
            product.products.forEach(pd => {
              if (selectedNcmId === pd?.product_id) {
                selectedPolicyPd = {
                  id: product?.id,
                  product: { ...pd },
                  vehicles: [...product?.vehicles],
                };
              }
            });
          });
        });
      setSelectedPolicyProduct(selectedPolicyPd);
    } else {
      setSelectedPolicyProduct(null);
    }
  }

  async function handleGetProducts(operationId, policyId) {
    try {
      const { data } = await cooperplaceApi.get(
        `products/forSelect?policyId=${policyId}&operationId=${operationId}`
      );
      if (data?.length === 1) {
        handleInitializeSingleOption(data, 'product', setData);
        handleSelectProduct(data[0]);
      }
      setProducts(data);
    } catch (error) {
      snackbar.show(<Text>Falha ao consultar produtos disponíveis!</Text>, {
        type: 'error',
      });
      navigate('/cargas');
    }
  }

  useEffect(() => {
    if (showRouteErrors && hasOrigin && hasDestination) {
      setShowRouteErrors(false);
    }
  }, [showRouteErrors, hasOrigin, hasDestination]);

  const extractPolicyValidationDestinationIndexes = useCallback((error, destinations = []) => {
    const destinationCount = Array.isArray(destinations) ? destinations.length : 0;
    if (destinationCount <= 0) return [];

    const indexes = new Set();
    const responseData = error?.response?.data;

    const candidateValues = [
      responseData?.error,
      ...(Array.isArray(responseData?.errors) ? responseData.errors : []),
      JSON.stringify(responseData || {}),
    ].filter(Boolean);

    candidateValues.forEach((value) => {
      const text = String(value);

      [...text.matchAll(/destinations?\[(\d+)\]/gi)].forEach((match) => {
        const idx = Number(match?.[1]);
        if (Number.isInteger(idx) && idx >= 0 && idx < destinationCount) {
          indexes.add(idx);
        }
      });

      [...text.matchAll(/destino\s*#?\s*(\d+)/gi)].forEach((match) => {
        const destinationPosition = Number(match?.[1]);
        if (Number.isInteger(destinationPosition)) {
          const idx = destinationPosition - 1;
          if (idx >= 0 && idx < destinationCount) {
            indexes.add(idx);
          }
        }
      });

      [...text.matchAll(/ponto\s*#?\s*(\d+)/gi)].forEach((match) => {
        const pointPosition = Number(match?.[1]);
        if (Number.isInteger(pointPosition)) {
          const idx = pointPosition - 2;
          if (idx >= 0 && idx < destinationCount) {
            indexes.add(idx);
          }
        }
      });
    });

    if (indexes.size > 0) {
      return Array.from(indexes);
    }

    const fallbackText = String(responseData?.error || responseData?.message || '').toLowerCase();
    
    if (fallbackText.includes('cidade de destino inválida') || fallbackText.includes('destino inválida')) {
      for (let i = 0; i < destinationCount; i++) {
        const destination = destinations[i];
        if (!destination?.city || !destination?.city?.id) {
          indexes.add(i);
        }
      }
      
      if (indexes.size > 0) {
        return Array.from(indexes);
      }
      
      return [0];
    }

    return [];
  }, []);

  function handleChangeCargoValue(value) {
    setData(old => ({ ...old, cargoValue: value }));
  }
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedCargo(data?.cargoValue);
    }, 1000);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [data?.cargoValue]);

  useEffect(() => {
    if (Number(debouncedCargo) > 0) {
      handleSearchRange();
    } else {
      setSelectedRange(null);
      setManagerTrackerOptions([]);
      setData(old => ({
        ...old,
        cargoValue: '0',
        manager_tracker: [],
        tracked: null,
        escorted: null,
      }));
    }
  }, [debouncedCargo]);

  async function handleSearchRange() {
    try {
      const policyId = selectedPolicy?.id || data.policy?.id
      const productId = selectedPolicyProduct?.id || data.policyProductId

      setData(old => ({
        ...old,
        manager_tracker: [],
        tracked: null,
        escorted: null,
      }));
      setLoadingRange(true);
      const response = await cooperplaceApi.get(
        `policies/${policyId}/ranges`,
        {
          params: {
            value: data?.cargoValue,
            policyProductId: productId,
            vehicleTypeIds: data?.vehicleTypes?.map(vt => vt.id),
            vehicleBodyTypeIds: data?.vehicleBodies?.map(vb => vb.id),
            vehicleImplementIds: data?.vehicleImplements?.map(vi => vi.id),
          },
        }
      );
      if (response?.data?.length === 0) {
        snackbar.show(
          <Text>
            O valor da mercadoria informada não se encaixa em nenhuma faixa de
            valores cadastradas na apólice selecionada!
          </Text>,
          { type: 'warning' }
        );
        setData(old => ({
          ...old,
          cargoValue: '0',
          manager_tracker: [],
          tracked: null,
          escorted: null,
        }));
        setLoadingRange(false);
        return;
      }
      const managerTrackers = response?.data[0]?.groups?.find(
        item => item?.code === 'manager_tracker'
      )?.items;
      setManagerTrackerOptions(managerTrackers);
      setSelectedRange(response.data[0]);
      setData(old => ({
        ...old,
        manager_tracker: managerTrackers ? [managerTrackers[0]] : [],
        tracked: response.data[0].tracked,
        escorted: response.data[0].equipments_groups?.every(
          item => item.escorted
        ),
      }));
    } catch (error) {
      //
    } finally {
      setLoadingRange(false);
    }
  }

  async function getDispositions() {
    try {
      const response = await fetchDispositionData();
      setLoadDispositionOptions(response);
    } catch (error) {
      setLoadDispositionOptions([]);
    }
  }

  async function getCountriesOptions(type, policyProductId) {
    try {
      const response = await cooperplaceApi.get(
        `countries?policyProductId=${policyProductId}&policyRouteType=${type}`
      );
      return response.data;
    } catch (error) {
      return [];
    }
  }

  async function getLocationsOptions(policyProductId) {
    try {
      const [originCountry, destinationsCountries] = await Promise.all([
        getCountriesOptions('origin', policyProductId),
        getCountriesOptions('destination', policyProductId),
      ]);
      if (originCountry?.length === 1 && !params.clone_id) {
        setData(old => ({
          ...old,
          origin: {
            ...old?.origin,
            country: originCountry[0],
          },
        }));
      }
      if (destinationsCountries?.length === 1 && !params.clone_id) {
        setData(old => ({
          ...old,
          destinations: [
            {
              address: '',
              type: DELIVERY_TYPE_OPTIONS[1],
              date: '',
              complement: '',
              lat: '',
              lng: '',
              formattedAddress: '',
              neighborhood: '',
              city: '',
              province: '',
              zipCode: '',
              country: destinationsCountries[0],
              number: '',
              startSchedule: '',
              index: 0,
            },
          ],
        }));
      }

      setCountriesOptions({
        origin: originCountry,
        destination: destinationsCountries,
      });
    } catch (error) {
      //
    }
  }

  useEffect(() => {
    if (rateResponse) {
      setDailyCotation({
        BRL: {
          value: rateResponse?.buy,
          label: 'Compra',
        },
        USD: {
          value: rateResponse?.sell,
          label: 'Venda',
        },
      });
    }
  }, [rateResponse]);


  const cargoValueInReal = useMemo(() => {
    const loadCurrency = data?.load_currency || currencyOptions[0]
    if (!dailyCotation || !data?.cargoValue || !loadCurrency?.value) {
      return 0;
    }

    const currency = dailyCotation[loadCurrency?.value];
    if (!currency) {
      return 0;
    }

    if (loadCurrency?.value === 'BRL') {
      return Number(data?.cargoValue);
    }

    return Number(data?.cargoValue) * Number(currency?.value);
  }, [data?.cargoValue, data?.load_currency, dailyCotation]);

  useEffect(() => {
    getDispositions();
    setData(old => ({
      ...old,
      loadCreationNumber: 1,
      operation: null,
      vehicleTypes: [],
      vehicleBodies: [],
    }));
  }, []);

  useEffect(() => {
    if (location?.state?.clientData && location?.state?.clientPolicies) {
      setData(old => ({ ...old, client: location?.state?.clientData }));
      handleGetClientOperations(location?.state?.clientPolicies);
    } else {
      snackbar.show(<Text>Acesso inválido!</Text>, { type: 'error' });
      navigate('/cargas');
    }
  }, [location]);

  useEffect(() => {
    if (data?.operation?.id && selectedPolicy?.id) {
      handleGetProducts(data?.operation?.id, selectedPolicy?.id);
    }
  }, [data?.operation?.id, selectedPolicy?.id]);

  useEffect(() => {
    if (!selectedPolicy?.id) return;
    if (!data?.product) return;
    if (selectedPolicyProduct?.id) return;

    handleSelectProduct(data.product);
  }, [selectedPolicy?.id, data?.product, selectedPolicyProduct?.id]);

  useEffect(() => {
    if (selectedPolicyProduct?.id) {
      getLocationsOptions(selectedPolicyProduct?.id);
    }
  }, [selectedPolicyProduct?.id]);

  function handleExtractProductVehiclesOptions(product) {
    const types = [];
    const bodies = [];
    const implement = [];

    product.vehicles.forEach(vehicleGroup => {
      vehicleGroup.vehicle_types.forEach(vehicle => {
        if (vehicle.type === 'vehicle') {
          if (!types.find(type => type.id === vehicle.id)) {
            types.push({
              ...vehicle,
              groupId: vehicleGroup.id,
            });
          }
        }
        if (vehicle.type === 'body') {
          if (!bodies.find(type => type.id === vehicle.id)) {
            bodies.push({
              ...vehicle,
              groupId: vehicleGroup.id,
            });
          }
        }
        if (vehicle.type === 'implement') {
          if (!implement.find(type => type.id === vehicle.id)) {
            implement.push({
              ...vehicle,
              groupId: vehicleGroup.id,
            });
          }
        }
      });
    });
    if (types?.length === 1) {
      handleInitializeSingleOption(types, 'vehicleTypes', setData, true);
    }
    if (bodies?.length === 1) {
      handleInitializeSingleOption(bodies, 'vehicleBodies', setData, true);
    }
    if (implement?.length === 1) {
      handleInitializeSingleOption(
        implement,
        'vehicleImplements',
        setData,
        true
      );
    }

    setVehicleTypes(types);
    setBodiesTypes(bodies);
    setImplementTypes(implement);
  }

  useEffect(() => {
    if (selectedPolicy?.id && selectedPolicyProduct?.id) {
      handleExtractProductVehiclesOptions(selectedPolicyProduct);
    }
  }, [selectedPolicy?.id, selectedPolicyProduct]);

  function validateAllDates(dataToValidate) {
    const invalidsMessages = {};

    const invalidsEndDate = validateDestinationsEndDate(dataToValidate?.destinations);
    const invalidsStartScheduleDate = validateDestinationsStartDate(dataToValidate?.destinations);
    const invalidEndStartDate = validateDestinationDate(dataToValidate?.destinations);
    const { start: invalidStart, end: invalidEnd } = validateDestinationDateWithOrigin(
      dataToValidate.origin,
      dataToValidate.destinations
    );
    const invalidSequential = validateSequentialDates([
      dataToValidate.origin,
      ...(dataToValidate.destinations || []),
    ]);

    if (invalidsEndDate.length > 0) {
      invalidsEndDate.forEach(invalidIdx => {
        invalidsMessages[`${invalidIdx}.scheduled_time`] =
          'Data máxima não pode ser inferior/igual ao destino anterior';
      });
    }

    if (invalidsStartScheduleDate.length > 0) {
      invalidsStartScheduleDate.forEach(invalidIdx => {
        invalidsMessages[`${invalidIdx}.start_schedule`] =
          'Data início não pode ser superior/igual ao destino posterior';
      });
    }

    if (invalidEndStartDate.length > 0) {
      invalidEndStartDate.forEach(invalidIdx => {
        invalidsMessages[`${invalidIdx}.start_schedule`] =
          'Data início entrega não pode ser menor que data máxima destino anterior';
      });
    }

    if (invalidStart?.length > 0) {
      invalidStart.forEach(invalidIdx => {
        invalidsMessages[`${invalidIdx}.start_schedule`] =
          'Data início entrega não pode ser menor que data da origem';
      });
    }

    if (invalidEnd?.length > 0) {
      invalidEnd.forEach(invalidIdx => {
        invalidsMessages[`${invalidIdx}.scheduled_time`] =
          'Data máxima entrega não pode ser menor que data da origem';
      });
    }

    if (invalidSequential.length > 0) {
      invalidSequential.forEach(invalidIdx => {
        const current = [dataToValidate.origin, ...(dataToValidate.destinations || [])][invalidIdx];
        const previous = [dataToValidate.origin, ...(dataToValidate.destinations || [])][invalidIdx - 1];

        const currentLabel = `#${invalidIdx + 1} ${current?.type?.name?.toUpperCase() || 'PONTO'}`;
        const previousLabel = `#${invalidIdx} ${previous?.type?.name?.toUpperCase() || 'PONTO'}`;

        const msg = `A data de início do ponto ${currentLabel} (${moment(current?.start_schedule).format('DD/MM/YYYY HH:mm')}) não pode ser menor que a data máxima do ponto ${previousLabel} (${moment(previous?.scheduled_time).format('DD/MM/YYYY HH:mm')}).`;

        invalidsMessages[`${invalidIdx}.start_schedule`] = msg;
      });
    }

    const hasErrors = Object.keys(invalidsMessages).length > 0;

    if (hasErrors) {
      setDestinationsError(invalidsMessages);
      return { isValid: false, messages: invalidsMessages };
    }

    setDestinationsError({});
    return { isValid: true, messages: {} };
  }

  function getRouteDateErrors(errorList = {}) {
    return Object.entries(errorList).reduce((acc, [path, message]) => {
      if (!message || !String(message).toLowerCase().includes('data')) return acc;

      const originField = path.match(/^origin\.(start_schedule|scheduled_time)$/);
      if (originField) {
        acc[`origin.${originField[1]}`] = message;
        return acc;
      }

      if (path === 'origin') {
        acc['origin.start_schedule'] = message;
        acc['origin.scheduled_time'] = message;
        return acc;
      }

      const destinationField = path.match(/^destinations\[(\d+)\]\.(start_schedule|scheduled_time)$/);
      if (destinationField) {
        acc[`${destinationField[1]}.${destinationField[2]}`] = message;
        return acc;
      }

      const destination = path.match(/^destinations\[(\d+)\]$/);
      if (destination) {
        acc[`${destination[1]}.start_schedule`] = message;
        acc[`${destination[1]}.scheduled_time`] = message;
      }

      return acc;
    }, {});
  }

  async function handleGoNext() {
    try {
      setLoadingGro(true);
      switch (currentStepIndex) {
        case 0: {
          await LoadDataGroSchema.validate(
            {
              loadCreationNumber: data?.loadCreationNumber,
              client: data?.client,
              costCenter: data?.costCenter,
              product: data?.product,
              branchOffice: data?.branchOffice,
              dueDate: data?.dueDate,
              operation: data?.operation,
              negotiation: data?.negotiation,
              negotiator: data?.negotiator,
              country: data?.country,
              phone: data?.phone,
            },
            { abortEarly: false }
          );
          break;
        }
        case 1: {
          setPolicyRouteErrorPointIds([]);

          await vehicleAndImplementTypesAreValid.validate(
            {
              vehicleTypes: data?.vehicleTypes,
              vehicleBodies: data?.vehicleBodies,
              vehicleImplements: data?.vehicleImplements,
            },
            {
              abortEarly: false,
            }
          );

          const points = [];
          const hasOriginPoint = isValidPoint(data?.origin);
          if (hasOriginPoint) points.push(data.origin);

          const validDests = Array.isArray(data?.destinations)
            ? data.destinations.filter(isValidPoint)
            : [];
          points.push(...validDests);

          const hasDestPoint = validDests.length > 0;
          if (!hasOriginPoint || !hasDestPoint) {
            const msg =
              !hasOriginPoint && !hasDestPoint
                ? 'Informe origem e ao menos um destino.'
                : !hasOriginPoint
                  ? 'Informe a origem.'
                  : 'Informe ao menos um destino.';

            setShowRouteErrors(true);
            setRouteErrorMessage(msg);
            snackbar.show(<Text>{msg}</Text>, { type: 'error' });
            setLoadingGro(false);
            return false;
          } else if (!data.polylineRoute) {
            const msg = 'A rota informada não é válida, verifique os endereços informados na roteirização';
            snackbar.show(<Text>{msg}</Text>, { type: 'error' });
            return false;
          } else {
            setShowRouteErrors(false);
            setRouteErrorMessage('');
          }

          const { isValid, messages } = validateAllDates({
            origin: data.origin,
            destinations: data.destinations,
          });

          if (!isValid) {
            setShowRouteErrors(true);
            setRouteErrorMessage('Verifique as datas dos destinos.');
            const msg = Object.values(messages).join(' | ');
            snackbar.show(
              <Text type="body" weight={500}>
                {msg || 'Verifique dados dos destinos!'}
              </Text>,
              { type: 'error' }
            );
            setLoadingGro(false);
            return false;
          } else {
            setShowRouteErrors(false);
            setRouteErrorMessage('');
          }

          await LocationsGroSchema.validate(
            {
              origin: data?.origin,
              destinations: data.destinations,
            },
            { abortEarly: false }
          );

          try {
            const policyId = selectedPolicy?.id || data.policy?.id
            await cooperplaceApi.post(
              `policies/${policyId}/validator`,
              {
                type: ['route'],
                operationId: data?.operation?.id,
                load: {
                  productId: data?.product?.id,
                  loadOrigins: {
                    cityId: data?.origin?.city?.id,
                  },
                  loadDestinations: data?.destinations.map(dest => ({
                    cityId: dest?.city?.id,
                  })),
                },
              }
            );
            setPolicyRouteErrorPointIds([]);
          } catch (error) {
            let message = 'Erro ao validar rota!';
            if (error?.response?.data?.error) {
              message = error?.response?.data?.error;
            } else if (error?.response?.data?.errors?.length) {
              message = error?.response?.data?.errors.join(' | ');
            }

            const invalidDestinationIndexes = extractPolicyValidationDestinationIndexes(
              error,
              data?.destinations,
            );

            const pointIdsFromDestinations = invalidDestinationIndexes
              .flatMap((destinationIndex) => {
                const point = data?.destinations?.[destinationIndex];
                const identifiers = [
                  point?.identifier,
                  point?.id,
                  destinationIndex + 1,
                ].filter((value) => value !== null && value !== undefined);

                return identifiers;
              });

            setPolicyRouteErrorPointIds(pointIdsFromDestinations);
            setShowRouteErrors(true);
            setRouteErrorMessage(message);
            snackbar.show(
              <Text type="body" weight={500}>
                {message}
              </Text>,
              {
                type: 'error',
              }
            );
            setLoadingGro(false);
            return false;
          }
          break;
        }
        case 2: {
          await VehicleInfosDataSchema.validate(
            {
              disposition: data?.disposition,
              pallets: data?.pallets,
              weight: data?.weight,
              cargoValue: data?.cargoValue,
              managerTrackerOptions,
              managerTracker: data?.manager_tracker,
            },
            {
              abortEarly: false,
            }
          );
          break;
        }
        default:
          break;
      }
      setErrorsGro({});
      setErrors({});
      setPolicyRouteErrorPointIds([]);
      setLoadingGro(false);
      return next();
    } catch (err) {
      const errorList = {};
      if (err.inner) {
        err.inner.forEach(error => {
          errorList[error.path] = error.message;
        });
        if (currentStepIndex === 3) {
          setErrors({ ...errorList });
        } else {
          setErrorsGro({ ...errorList });

          if (currentStepIndex === 1) {
            const routeDateErrors = getRouteDateErrors(errorList);
            setDestinationsError(routeDateErrors);

            const routeDateErrorMessages = [...new Set(Object.values(routeDateErrors))];
            if (routeDateErrorMessages.length > 0) {
              snackbar.show(
                <Text type="body" weight={500}>
                  {routeDateErrorMessages.join(' | ')}
                </Text>,
                { type: 'error' }
              );
            }
          }
        }
        setLoadingGro(false);
        return false;
      }
      snackbar.show(<Text>Falha inesperada ao validar.</Text>, { type: 'error' });
      setLoadingGro(false);
      return false;
    }
  }

  async function handleAttemptToSave() {
    try {
      setLoadingGro(true);
      const freightValues = await freightValuesRef?.current?.getValues();
      if (!freightValues) {
        setLoadingGro(false);
        return;
      }

      const replacementFreightValues =
        await freightValuesReplaceRef?.current?.getValues();
      if (data?.isReplacement && !replacementFreightValues) {
        setLoadingGro(false);
        return;
      }

      if (
        !(await checkClientCreditLimit(
          data?.client?.id,
          +data?.total_taker_value,
          setErrors
        ))
      ) {
        snackbar.show(
          <Text>
            Cliente sem limite de crédito! Não é possível cadastrar carga para
            tomadores sem limite de crédito!
          </Text>,
          { type: 'error' }
        );
        setLoadingGro(false);
        return;
      }

      await handleCreate(
        {
          ...data,
          dailyCotation: dailyCotation[data?.load_currency?.value]?.value,
          policyId: selectedPolicy?.id,
          policyRangeId: selectedRange?.id,
          policyOperationId: data?.operation?.id,
          policyVehicleId: data?.vehicleTypes[0]?.groupId,
          extraFields,
          inputsExtraFields,
          transferExtraFields,
          discountsExtraFields,
        },
        tollsPlazaInfos,
        freightValues,
        replacementFreightValues,
        setLoadingGro,
        setShowModal,
        setRegisteredLoad
      );
    } catch (error) {
      const message =
        error?.response?.data?.message ?? 'Erro ao cadastrar carga!';
      snackbar.show(<Text>{message}</Text>, { type: 'error' });
      setLoadingGro(false);
    }
  }

  const lastDestination = useMemo(() => {
    return data?.destinations?.length
      ? data?.destinations[data?.destinations?.length - 1]
      : null;
  }, [data?.destinations]);

  async function onChangeInsurancePropsFilter() {
    const insuranceValue = await updateInsuranceValue({
      document: data?.client?.cgccpf,
      container: data?.container,
      loadValue: debouncedCargo,
      currency: data?.load_currency?.value,
      exchange: rateResponse?.sell,
      originIbgeCode: data?.origin?.city?.ibge_code,
      originCityName: data?.origin?.city?.name,
      originState: data?.origin?.city?.province?.uf,
      originStateName: data?.origin?.city?.province?.name,
      originCountry: data?.origin?.country?.abbreviation,
      destinationIbgeCode: lastDestination?.city?.ibge_code,
      destinationCityName: lastDestination?.city?.name,
      destinationState: lastDestination?.city?.province?.uf,
      destinationStateName: lastDestination?.city?.province?.name,
      destinationCountry: lastDestination?.country?.abbreviation,
    });

    if (insuranceValue !== null && insuranceValue !== undefined) {
      await freightValuesRef?.current?.setInsuranceValueBenner(insuranceValue);
      await freightValuesReplaceRef?.current?.setInsuranceValueBenner(insuranceValue);
    }
  }

  useEffect(() => {
    if (location?.pathname.includes('gro')) {
      onChangeInsurancePropsFilter();
    }
  }, [
    location,
    debouncedCargo,
    rateResponse?.sell,
    data?.load_currency?.value,
    data?.client?.cgccpf,
    data?.container,
    data?.origin?.city,
    data?.origin?.country,
    lastDestination?.city,
    lastDestination?.country,
  ]);
  const isNational = useMemo(() => {
    return data?.origin?.country?.abbreviation === 'br' && lastDestination?.country?.abbreviation === 'br';
  }, [data?.origin?.country?.abbreviation, lastDestination?.country?.abbreviation]);

  return (
    <LoadGroProvider
      value={{
        data,
        setData,
        selectedPolicy,
        setSelectedPolicy,
        operations,
        handleGetSelectedPolicy,
        disableProduct,
        products,
        setProducts,
        errorsGro,
        setErrorsGro,
        countryOptions,
        destinationsError,
        setDestinationsError,
        selectedPolicyProduct,
        setSelectedPolicyProduct,
        vehicleTypes,
        bodiesTypes,
        implementTypes,
        loadDispositionOptions,
        dailyCotation,
        handleChangeCargoValue,
        handleSelectProduct,
        handleSearchRange,
        selectedRange,
        loadingRange,
        managerTrackerOptions,
        tollsPlazaInfos,
        setTollsPlazaInfos,
        setTollsPlazaLoading,
        loadingSelectedPolicy
      }}
    >
      <Card loading={loadingDuplicate}>
        <>{!isLastStep ? step : null}</>
        <div style={{ display: isLastStep ? 'block' : 'none' }}>
          <FreightValuesGRO
            freightValuesRef={freightValuesRef}
            freightValuesReplaceRef={freightValuesReplaceRef}
            cargoValue={cargoValueInReal}
            isReplacement={data?.isReplacement}
            isNational={isNational}
          />
        </div>
        <div
          style={{
            flex: 1,
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Button
            type="button"
            onClick={back}
            variant="secondary"
            disabled={isFirstStep || loadingGro || insuranceBennerLoading || tollsPlazaLoading || loadingSelectedPolicy}
            id="button-gro-passo-anterior"
          >
            <Text type="regular">Anterior</Text>
          </Button>

          <StepsContainer>
            <Text color="dark">
              Etapa {currentStepIndex + 1}/{steps?.length}{' '}
            </Text>
            <ProgressBarContainer>
              {Array.from({ length: steps?.length }).map((_, index) => (
                <StepContainer key={index}>
                  <Step completed={index <= currentStepIndex} />
                  {index !== steps?.length - 1 ? (
                    <Line completed={index < currentStepIndex} />
                  ) : null}
                </StepContainer>
              ))}
            </ProgressBarContainer>
          </StepsContainer>

          {isLastStep ? (
            <Button
              type="button"
              onClick={() => {
                handleAttemptToSave();
              }}
              variant="success"
              loading={loadingGro || insuranceBennerLoading || tollsPlazaLoading}
              id="button-gro-salvar"
            >
              <Text>Salvar</Text>
            </Button>
          ) : (
            <Button
              onClick={() => handleGoNext()}
              loading={insuranceBennerLoading || tollsPlazaLoading || loadingGro || loadingSelectedPolicy}
              id="button-gro-passo-posterior"
            >
              <Text color="white" type="regular">
                Próximo
              </Text>
            </Button>
          )}
        </div>
      </Card>
    </LoadGroProvider>
  );
}
